import React, { useEffect, useRef, useState } from "react";
import passportIconSvg from "assets/images/passport-icon-in-guidelines.svg";
import shutterButtonSvg from "assets/images/shutter-button.svg";
import { Box, Icon, Text } from "components";
import useCamera from "hooks/useCamera";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { getTranslationsStyle } from "utils/arabisation.util";

const FullScreenCamera = styled.div`
  top: 0;
  height: 100svh;
  width: 100%;
  position: fixed;
  overflow: hidden;
  z-index: ${(props) => props.theme.zIndex.modalClose};
  background-color: ${(props) => props.theme.colors.backgroundQuaternary};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

// Full-width passport scanning window
const ScanWindowOverlay = styled.div`
  outline: 9999px solid rgba(0, 0, 0, 0.5);
  border-radius: 20px;
  position: absolute;
  left: 0;
  top: 80px;
  height: 45%;
  width: 100%;
`;

// Wrapper that mirrors ScanWindowOverlay position to hold corner brackets
const ScanWindowCorners = styled.div`
  position: absolute;
  left: 0;
  top: 80px;
  height: 45%;
  width: 100%;
  pointer-events: none;
  z-index: 1;
`;

// Reusable corner bracket decoration
const CornerBracket = styled.div`
  position: absolute;
  width: 110px;
  height: 86px;
  pointer-events: none;

  ${({ $position }) => {
    switch ($position) {
      case "top-left":
        return `
          top: 0; left: 0;
          border-top: 4px solid white;
          border-left: 4px solid white;
          border-top-left-radius: 20px;
        `;
      case "top-right":
        return `
          top: 0; right: 0;
          border-top: 4px solid white;
          border-right: 4px solid white;
          border-top-right-radius: 20px;
        `;
      case "bottom-left":
        return `
          bottom: 0; left: 0;
          border-bottom: 4px solid white;
          border-left: 4px solid white;
          border-bottom-left-radius: 20px;
        `;
      case "bottom-right":
        return `
          bottom: 0; right: 0;
          border-bottom: 4px solid white;
          border-right: 4px solid white;
          border-bottom-right-radius: 20px;
        `;
      default:
        return "";
    }
  }}
`;

const ShutterButtonStyled = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  flex-shrink: 0;

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function OCRScanner({ onScanComplete, onClose, pauseScanner, scanAnalytics }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);

  useCamera(videoRef);

  const handleCaptureImage = async () => {
    if (
      pauseScanner.current ||
      !videoRef.current ||
      !canvasRef.current ||
      isCapturing
    ) {
      return;
    }

    const video = videoRef.current;

    // Make sure video is ready
    if (!video.videoWidth || !video.videoHeight) {
      console.error("Video not ready");
      return;
    }

    setIsCapturing(true);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Map overlay screen coordinates to native video coordinates.
    // Reason: the video uses objectFit:"cover" which scales and center-crops
    // the feed to fill the container. We must reverse that transform so the
    // crop region on the canvas matches exactly what the user sees through
    // the ScanWindowOverlay on screen.
    const containerWidth = video.clientWidth;
    const containerHeight = video.clientHeight;
    const videoNativeWidth = video.videoWidth;
    const videoNativeHeight = video.videoHeight;

    const scaleX = containerWidth / videoNativeWidth;
    const scaleY = containerHeight / videoNativeHeight;
    const coverScale = Math.max(scaleX, scaleY);

    // Visible region offset (objectFit: cover centres the video)
    const offsetX = (videoNativeWidth - containerWidth / coverScale) / 2;
    const offsetY = (videoNativeHeight - containerHeight / coverScale) / 2;

    // ScanWindowOverlay CSS values: left:0, top:80px, width:100%, height:45%
    const overlayLeft = 0;
    const overlayTop = 80;
    const overlayWidth = containerWidth;
    const overlayHeight = containerHeight * 0.45;

    // Convert screen-space overlay rect to native video-pixel rect
    const cropX = Math.max(0, Math.round(offsetX + overlayLeft / coverScale));
    const cropY = Math.max(0, Math.round(offsetY + overlayTop / coverScale));
    const cropWidth = Math.min(
      Math.round(overlayWidth / coverScale),
      videoNativeWidth - cropX
    );
    const cropHeight = Math.min(
      Math.round(overlayHeight / coverScale),
      videoNativeHeight - cropY
    );

    // Create a new canvas for the cropped image
    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = cropWidth;
    croppedCanvas.height = cropHeight;
    const croppedCtx = croppedCanvas.getContext("2d");

    // Draw the cropped region
    croppedCtx.drawImage(
      canvas,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    // Apply grayscale and enhance contrast
    const imageData = croppedCtx.getImageData(
      0,
      0,
      croppedCanvas.width,
      croppedCanvas.height
    );
    const pixels = imageData.data;

    // Convert to grayscale with enhanced contrast
    for (let i = 0; i < pixels.length; i += 4) {
      // Calculate grayscale value (weighted average for better perception)
      const gray =
        0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];

      // Apply strong contrast enhancement (factor of 1.8 for much sharper text)
      const contrastFactor = 1.8;
      const enhancedGray = Math.min(
        255,
        Math.max(0, (gray - 128) * contrastFactor + 128)
      );

      // Set RGB to the same grayscale value
      pixels[i] = enhancedGray; // Red
      pixels[i + 1] = enhancedGray; // Green
      pixels[i + 2] = enhancedGray; // Blue
      // Alpha (pixels[i + 3]) remains unchanged
    }

    // Put the processed image data back
    croppedCtx.putImageData(imageData, 0, 0);

    let processedCanvas = croppedCanvas;

    // Convert original canvas to blob
    canvas.toBlob(
      (originalBlob) => {
        if (!originalBlob) {
          console.error("Failed to create original image blob");
          setIsCapturing(false);
          return;
        }

        // Convert processed canvas to blob
        processedCanvas.toBlob(
          (processedBlob) => {
            if (!processedBlob) {
              console.error("Failed to create processed image blob");
              setIsCapturing(false);
              return;
            }

            // Mark analytics
            if (scanAnalytics && scanAnalytics.markFrameSent) {
              scanAnalytics.markFrameSent();
            }

            // Send both images to parent component immediately
            onScanComplete?.({
              originalImage: originalBlob,
              processedImage: processedBlob,
            });
          },
          "image/jpeg",
          0.95
        );
      },
      "image/jpeg",
      0.95
    );
  };

  useEffect(() => {
    if (scanAnalytics && scanAnalytics.markCameraOpened) {
      scanAnalytics.markCameraOpened();
    }
  }, [scanAnalytics]);

  return (
    <FullScreenCamera>
      <VideoContainer>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        <Box
          position="absolute"
          top="calc(80px + 45%)"
          bottom="0"
          left="0"
          w="100%"
          d="flex"
          direction="column"
          alignItems="center"
          style={{ zIndex: 1 }}
        >
          {/* Remaining space above icon fills up to scan window bottom */}
          <Box flex="1" pT="m" />
          <img
            src={passportIconSvg}
            alt="Passport"
            style={{ width: "110px", height: "auto", flexShrink: 0 }}
          />
          <Text
            color="textQuaternary"
            fontSize="l"
            textAlign="center"
            mT="m"
            style={{
              ...getTranslationsStyle(i18n, { textAlign: "center" }),
              maxWidth: "70vw",
              flexShrink: 0,
            }}
          >
            {t("smart_gate.scanning_instructions")}
          </Text>
          <Box h="4vh" flex="0 0 4vh" />
          <ShutterButtonStyled
            onClick={handleCaptureImage}
            disabled={isCapturing}
            aria-label="Capture passport image"
          >
            <img
              src={shutterButtonSvg}
              alt="Capture"
              style={{ width: "80px", height: "80px" }}
            />
          </ShutterButtonStyled>
          <Box h="4vh" flex="0 0 4vh" />
        </Box>
        <ScanWindowOverlay />
        <ScanWindowCorners>
          <CornerBracket $position="top-left" />
          <CornerBracket $position="top-right" />
          <CornerBracket $position="bottom-left" />
          <CornerBracket $position="bottom-right" />
        </ScanWindowCorners>
        <Box
          onClick={onClose}
          position="absolute"
          top="30px"
          left="30px"
          style={{ zIndex: theme.zIndex.modalClose }}
        >
          <Icon size="xl" color="backgroundQuaternary" name="Close" />
        </Box>
      </VideoContainer>
    </FullScreenCamera>
  );
}

export default OCRScanner;
